import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmActionDialog, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { networkErrorMessage, type NetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { BrandDetail } from '../../../common/hooks/network-admin.types';
import type { NetworkBrand } from '../../../common/models/network.model';
import { ErrorNote, FIELD_LABEL, vocabLabel } from './network-parts';

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The names this brand goes by on retailers' sites ("Aurora", "Ceramiche
 * Aurora SpA"…): the matcher reads a retailer's brand field against them. The
 * brand's own name is always one of them (the API keeps it).
 */
export function AliasesEditor({ brand, api, onSaved }: { brand: NetworkBrand; api: NetworkAdminApi; onSaved: (b: NetworkBrand) => void }) {
    const { t } = useTranslation();
    const [aliases, setAliases] = useState<string[]>(brand.aliases || []);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => setAliases(brand.aliases || []), [brand.aliases]);

    const add = () => {
        const v = input.trim();
        if (!v) return;
        if (!aliases.some((a) => a.toLowerCase() === v.toLowerCase())) setAliases((as) => [...as, v]);
        setInput('');
    };
    const isName = (a: string) => a.toLowerCase() === (brand.name || '').toLowerCase();
    const dirty = !sameList(aliases, brand.aliases || []);

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const saved = await api.updateBrand(brand._id, { aliases });
            if (!saved?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            onSaved(saved);
            toast.success(t('ADMIN_NETWORK.BRAND.ALIASES_SAVED'));
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SectionCard
            title={t('ADMIN_NETWORK.BRAND.ALIASES')}
            description={t('ADMIN_NETWORK.BRAND.ALIASES_HINT')}
            action={
                dirty ? (
                    <div className="flex gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => setAliases(brand.aliases || [])} disabled={busy}>{t('ADMIN_SHELL.COMMON.DISCARD')}</Button>
                        <Button variant="outline" size="sm" onClick={save} disabled={busy} data-testid="aliases-save">{t('ADMIN_SHELL.COMMON.SAVE')}</Button>
                    </div>
                ) : undefined
            }
            testId="brand-aliases"
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-1.5">
                    {aliases.map((a) => (
                        <span key={a} className="inline-flex h-7 items-center gap-1 rounded-md border bg-background ps-2 pe-1 text-xs" data-testid="alias-chip">
                            {a}
                            {isName(a) ? (
                                <span className="px-1 text-muted-foreground">{t('ADMIN_NETWORK.BRAND.ALIAS_NAME')}</span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setAliases((as) => as.filter((x) => x !== a))}
                                    className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                                    aria-label={t('ADMIN_NETWORK.BRAND.ALIAS_REMOVE', { alias: a })}
                                >
                                    <X className="size-3" />
                                </button>
                            )}
                        </span>
                    ))}
                    {!aliases.length && <span className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.BRAND.NO_ALIASES')}</span>}
                </div>
                <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        add();
                    }}
                >
                    <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('ADMIN_NETWORK.BRAND.ALIAS_PLACEHOLDER')} aria-label={t('ADMIN_NETWORK.BRAND.ALIAS_PLACEHOLDER')} className="h-8 sm:max-w-xs" data-testid="alias-input" />
                    <Button type="submit" variant="outline" size="sm" disabled={!input.trim()} className="h-8">
                        <Plus />
                        {t('ADMIN_NETWORK.COMMON.ADD')}
                    </Button>
                </form>
                {error && <ErrorNote message={error} />}
            </div>
        </SectionCard>
    );
}

/** Country, website, description. */
export function BrandDetailsForm({ brand, api, onSaved }: { brand: NetworkBrand; api: NetworkAdminApi; onSaved: (b: NetworkBrand) => void }) {
    const { t } = useTranslation();
    const initial = { country: brand.country || '', website: brand.website || '', description: brand.description || '' };
    const [draft, setDraft] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => setDraft({ country: brand.country || '', website: brand.website || '', description: brand.description || '' }), [brand.country, brand.website, brand.description]);

    const dirty = draft.country !== initial.country || draft.website !== initial.website || draft.description !== initial.description;

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const saved = await api.updateBrand(brand._id, {
                country: draft.country.trim().toUpperCase(),
                website: draft.website.trim(),
                description: draft.description.trim(),
            });
            if (!saved?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            onSaved(saved);
            toast.success(t('ADMIN_NETWORK.BRAND.DETAILS_SAVED'));
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SectionCard
            title={t('ADMIN_NETWORK.BRAND.DETAILS')}
            action={
                dirty ? (
                    <div className="flex gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => setDraft(initial)} disabled={busy}>{t('ADMIN_SHELL.COMMON.DISCARD')}</Button>
                        <Button variant="outline" size="sm" onClick={save} disabled={busy} data-testid="details-save">{t('ADMIN_SHELL.COMMON.SAVE')}</Button>
                    </div>
                ) : undefined
            }
            testId="brand-details"
        >
            <div className="grid gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[8rem_1fr]">
                    <div className="grid gap-1.5">
                        <Label htmlFor="bd-country" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.COUNTRY')}</Label>
                        <Input id="bd-country" maxLength={2} value={draft.country} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))} />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="bd-website" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.WEBSITE')}</Label>
                        <Input id="bd-website" dir="ltr" type="url" value={draft.website} placeholder="https://" onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))} />
                    </div>
                </div>
                <div className="grid gap-1.5">
                    <Label htmlFor="bd-description" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.DESCRIPTION')}</Label>
                    <Textarea id="bd-description" rows={3} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
                </div>
                {error && <ErrorNote message={error} />}
            </div>
        </SectionCard>
    );
}

type Collection = BrandDetail['collections'][number];

/** The brand's collections: add, rename, delete (only an empty one — the API refuses otherwise and says so). */
export function CollectionsCard({ brandId, collections, api, onChanged }: { brandId: string; collections: Collection[]; api: NetworkAdminApi; onChanged: () => void }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState<Collection | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const add = async () => {
        if (!name.trim()) return;
        setAdding(true);
        setError(null);
        try {
            await api.createCollection(brandId, { name: name.trim() });
            setName('');
            onChanged();
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setAdding(false);
        }
    };

    const rename = async () => {
        if (!editing || !editing.name.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await api.updateCollection(editing.id, { name: editing.name.trim() });
            setEditing(null);
            onChanged();
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!deleting) return;
        setBusy(true);
        setDeleteError(null);
        try {
            await api.deleteCollection(deleting._id);
            setDeleting(null);
            onChanged();
            toast.success(t('ADMIN_NETWORK.COLLECTIONS.DELETED'));
        } catch (e) {
            setDeleteError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SectionCard title={t('ADMIN_NETWORK.COLLECTIONS.TITLE')} description={t('ADMIN_NETWORK.COLLECTIONS.HINT', { count: collections.length })} testId="brand-collections">
            <div className="flex flex-col gap-3">
                {collections.length ? (
                    <ul className="flex flex-col divide-y rounded-lg border">
                        {collections.map((c) => (
                            <li key={c._id} className="flex items-center gap-2 px-3 py-2" data-testid={`collection-${c.slug || c._id}`}>
                                {editing?.id === c._id ? (
                                    <form
                                        className="flex flex-1 items-center gap-1.5"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void rename();
                                        }}
                                    >
                                        <Input
                                            autoFocus
                                            value={editing.name}
                                            onChange={(e) => setEditing({ id: c._id, name: e.target.value })}
                                            onKeyDown={(e) => e.key === 'Escape' && setEditing(null)}
                                            className="h-7"
                                            aria-label={t('ADMIN_NETWORK.COLLECTIONS.RENAME')}
                                        />
                                        <Button type="submit" variant="outline" size="icon-sm" disabled={busy || !editing.name.trim()} aria-label={t('ADMIN_SHELL.COMMON.SAVE')}>
                                            <Check />
                                        </Button>
                                        <Button type="button" variant="ghost" size="icon-sm" onClick={() => setEditing(null)} aria-label={t('ADMIN_SHELL.COMMON.CANCEL')}>
                                            <X />
                                        </Button>
                                    </form>
                                ) : (
                                    <>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm">{c.name}</p>
                                            <p className="truncate text-xs text-muted-foreground">
                                                {[t('ADMIN_NETWORK.COLLECTIONS.PRODUCTS_N', { count: c.products ?? 0, formatted: formatNumber(c.products ?? 0) }), c.material && vocabLabel(t, c.material), c.appearance && vocabLabel(t, c.appearance)]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="icon-sm" onClick={() => setEditing({ id: c._id, name: c.name })} aria-label={t('ADMIN_NETWORK.COLLECTIONS.RENAME_N', { name: c.name })}>
                                            <Pencil />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            className="text-muted-foreground hover:text-destructive"
                                            onClick={() => {
                                                setDeleteError(null);
                                                setDeleting(c);
                                            }}
                                            aria-label={t('ADMIN_NETWORK.COLLECTIONS.DELETE_N', { name: c.name })}
                                        >
                                            <Trash2 />
                                        </Button>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.COLLECTIONS.NONE')}</p>
                )}
                <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void add();
                    }}
                >
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('ADMIN_NETWORK.COLLECTIONS.NEW_PLACEHOLDER')} aria-label={t('ADMIN_NETWORK.COLLECTIONS.NEW_PLACEHOLDER')} className="h-8" data-testid="collection-input" />
                    <Button type="submit" variant="outline" size="sm" className="h-8" disabled={adding || !name.trim()}>
                        <Plus />
                        {t('ADMIN_NETWORK.COMMON.ADD')}
                    </Button>
                </form>
                {error && <ErrorNote message={error} />}
            </div>

            <ConfirmActionDialog
                open={!!deleting}
                onOpenChange={(o) => !o && setDeleting(null)}
                title={t('ADMIN_NETWORK.COLLECTIONS.DELETE_TITLE', { name: deleting?.name || '' })}
                description={t('ADMIN_NETWORK.COLLECTIONS.DELETE_BODY')}
                confirmLabel={t('ADMIN_SHELL.COMMON.DELETE')}
                destructive
                busy={busy}
                onConfirm={remove}
            >
                {deleteError && <ErrorNote message={deleteError} />}
            </ConfirmActionDialog>
        </SectionCard>
    );
}

